import React, { useEffect, useState } from "react";
import { Modal, Button, Form, message } from "antd";
import ChartCreateForm from "../Form";
import {
  useChartCreate,
  useChartGetById,
  useChartTableGet,
  useChartUpdate,
} from "../../../../../../../hooks/api/charts";
import dayjs from "dayjs";
import { useVaadTabs } from "../../context";
import {
  useMessageError,
  useMessageSuccess,
} from "../../../../../../../hooks/common";
import { useVaadGetById } from "../../../../../../../hooks/api/vaads";
import { useMeetingTableGet } from "../../../../../../../hooks/api/meetings";

interface ChartModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  vaadId: string;
  chartId?: string;
  mode?: "create" | "edit";
}

export interface ExerciseItem {
  id: number;
  exerciseId?: string;
  repetitions?: number;
}

export default function ChartModal({
  open,
  onClose,
  onSave,
  vaadId,
  chartId,
  mode = "create",
}: ChartModalProps): React.ReactElement {
  const [form] = Form.useForm();
  const [exercises, setExercises] = useState<ExerciseItem[]>([{ id: 1 }]);
  const chartCreate = useChartCreate();
  const chartUpdate = useChartUpdate();
  const chartGetById = useChartGetById();
  const prevChartGetById = useChartGetById();
  const chartTableGet = useChartTableGet();
  const meetingTableGet = useMeetingTableGet();
  const vaadGetById = useVaadGetById(vaadId);
  const { vaadData, setVaadData, selectedMiddahId } = useVaadTabs();
  const vaadType = vaadData?.type;

  // Defaults for a brand new chart: the vaad's currently studied middah and the
  // exercises carried over from the previous chart of that middah.
  const [createMiddah, setCreateMiddah] = useState<
    { id: string; name: string } | undefined
  >();
  const [createExercises, setCreateExercises] = useState<
    { id: string; name: string }[] | undefined
  >();

  // Resolve the currently studied middah the same way OverviewSection does:
  // match selectedMiddahId against THIS vaad's learnedMiddahs (selectedMiddahId
  // persists across vaads, so it may be stale), then fall back to the vaad's
  // own middah. If neither resolves, there is no current middah ("None").
  const currentMiddah =
    vaadData?.learnedMiddahs?.find((m) => m.middah.id === selectedMiddahId)
      ?.middah ||
    vaadData?.middah ||
    undefined;
  const currentMiddahId = currentMiddah?.id;

  useEffect(() => {
    if (mode === "edit" && chartId && open) {
      chartGetById.fetch(undefined, chartId);
    }
  }, [mode, chartId, open]);

  useEffect(() => {
    if (mode === "edit" && chartGetById.data) {
      const chart = chartGetById.data;

      const chartExercises = chart.avodas?.map((avoda, index) => ({
        id: index + 1,
        exerciseId: avoda.exercise?.id,
        repetitions: avoda.repetitionsPerWeek,
      })) || [{ id: 1 }];

      setExercises(chartExercises);

      form.setFieldsValue({
        name: chart.name,
        startDate: chart.startTime ? dayjs(chart.startTime) : null,
        endDate: chart.endTime ? dayjs(chart.endTime) : null,
        middahId: chart.middah?.id,
        exercises: Object.fromEntries(
          chartExercises.map((ex) => [
            String(ex.id),
            { exerciseId: ex.exerciseId, repetitions: ex.repetitions },
          ]),
        ),
      });
    }
  }, [mode, chartGetById.data, form]);

  useEffect(() => {
    if (mode !== "create" || !open || !currentMiddahId) return;

    const middahName = currentMiddah?.name || "";

    chartTableGet
      .fetch({
        vaadId,
        middahId: currentMiddahId,
        page: 1,
        pageSize: 1,
        orderByColumn: "createdAt",
        orderBy: "DESC",
      })
      .then(async (response) => {
        const total = response?.total || 0;
        // From the list take only the previous chart's id; the actual avoda
        // data (with exercise names) comes from the getById endpoint, since the
        // list response doesn't include full avoda details.
        const previousChartId = response?.data?.[0]?.id;
        const defaultName = middahName
          ? `${middahName} Chart ${total + 1}`
          : "";

        setCreateMiddah({ id: currentMiddahId, name: middahName });
        form.setFieldsValue({ name: defaultName, middahId: currentMiddahId });

        const previousChart = previousChartId
          ? await prevChartGetById.fetch(undefined, previousChartId)
          : undefined;

        const carriedExercises: ExerciseItem[] = previousChart?.avodas?.length
          ? previousChart.avodas.map((avoda, index) => ({
              id: index + 1,
              exerciseId: avoda.exercise?.id,
              repetitions: avoda.repetitionsPerWeek,
            }))
          : [{ id: 1 }];

        setExercises(carriedExercises);
        setCreateExercises(previousChart?.avodas?.map((a) => a.exercise));

        form.setFieldsValue({
          exercises: Object.fromEntries(
            carriedExercises.map((ex) => [
              String(ex.id),
              { exerciseId: ex.exerciseId, repetitions: ex.repetitions },
            ]),
          ),
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, open, currentMiddahId]);

  // Pre-fill the chart dates from the upcoming meetings: start date = closest
  // future meeting, end date = one day before the meeting immediately after
  // that (charts must not overlap). This is independent of the middah, so it
  // runs even when there is no currently studied middah.
  useEffect(() => {
    if (mode !== "create" || !open) return;

    meetingTableGet
      .fetch({
        vaadId,
        period: "future",
        page: 1,
        pageSize: 2,
        orderByColumn: "startTime",
        orderBy: "ASC",
      })
      .then((response) => {
        const meetings = response?.data || [];
        const [first, second] = meetings;

        form.setFieldsValue({
          startDate: first?.startTime ? dayjs.utc(first.startTime) : undefined,
          endDate: second?.startTime
            ? dayjs.utc(second.startTime).subtract(1, "day")
            : undefined,
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, open]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // exercises is keyed by exercise id (see Form.Item names), so take values.
      const formExercises = Object.values(values.exercises || {});

      const payload = {
        name: values.name,
        startTime: values.startDate?.startOf("day").toISOString(),
        endTime: values.endDate?.startOf("day").toISOString(),
        vaad: { id: vaadId },
        middah: { id: values.middahId },
        defaultTasks: ["check-in", "reading"],
        avodas: formExercises
          .filter((e: any) => e?.exerciseId)
          .map((e: any) => ({
            exercise: { id: e.exerciseId },
            repetitionsPerWeek: e.repetitions || 1,
          })),
      };

      let result;
      if (mode === "edit" && chartId) {
        result = await chartUpdate.fetch(payload, chartId);
      } else {
        result = await chartCreate.fetch(payload);
      }
      if (result instanceof Error || (result as any)?.isAxiosError) {
        return;
      }
      const freshData = await vaadGetById.fetch(undefined, vaadId);
      if (freshData) {
        setVaadData(freshData);
      }

      onSave();
      onClose();
      form.resetFields();
      setExercises([{ id: 1 }]);
    } catch (error) {
      console.error(error);
    }
  };

  useMessageSuccess([chartCreate], "Chart created successfully");
  useMessageError([chartCreate]);
  useMessageSuccess([chartUpdate], "Chart updated successfully");
  useMessageError([chartUpdate]);

  const handleClose = () => {
    form.resetFields();
    setExercises([{ id: 1 }]);
    onClose();
  };

  return (
    <Modal
      title={mode === "edit" ? "Edit Chart" : "Create New Chart"}
      open={open}
      onCancel={handleClose}
      width={560}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSubmit}
          loading={mode === "edit" ? chartUpdate.loading : chartCreate.loading}
        >
          {mode === "edit" ? "Save Changes" : "Save"}
        </Button>,
      ]}
    >
      <Form form={form} component={false}>
        <ChartCreateForm
          vaadId={vaadId}
          vaadType={vaadType}
          exercises={exercises}
          setExercises={setExercises}
          initialMiddah={
            mode === "edit" ? chartGetById.data?.middah : createMiddah
          }
          initialExercises={
            mode === "edit"
              ? chartGetById.data?.avodas?.map((a) => a.exercise)
              : createExercises
          }
        />
      </Form>
    </Modal>
  );
}
